// src/App.jsx
import React, { useEffect, useState, useRef, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import activityTracker from "./lib/activityTracker";
import { UpdateNotification } from "./components/UpdateNotification";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import AuthCallback from "./pages/AuthCallback";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { AccessProvider } from "./contexts/AccessContext";
import { SimulatorProvider } from "./contexts/SimulatorContext";
import RequireAccess from "./components/RequireAccess";
import RequireNotFieldVendor from "./components/RequireNotFieldVendor";
import SessionExpiredModal from "./auth/SessionExpiredModal";
import { refreshAccessToken } from "./utils/auth";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { currentHomePath } from "./utils/homePath";

// Code-splitting por ruta: cada página va en su propio chunk y se descarga al
// navegar a ella. El bundle inicial queda en login + shell + providers, de modo
// que el arranque es pequeño y resiliente en redes flojas (antes: main.js único
// con TODO el simulador dentro, todo-o-nada). El árbol completo del SIMULADOR
// (~8MB de páginas mock) sale del main vía Layout, que carga SimulatorApp lazy.
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"), "dashboard");
const Personal = lazyWithRetry(() => import("./pages/Personal"), "personal");
// import Automatizaciones from "./pages/Automatizaciones";
const Campañas = lazyWithRetry(() => import("./pages/Campañas"), "campanas");
const SeguimientoCampañas = lazyWithRetry(() => import("./pages/SeguimientoCampañas"), "seguimiento-campanas");
const KanbanCampana = lazyWithRetry(() => import("./pages/KanbanCampaña"), "kanban-campana");
const Chats = lazyWithRetry(() => import("./pages/Chats"), "chats");
const Documentos = lazyWithRetry(() => import("./pages/Documentos"), "documentos");
// import Contactos from "./pages/Contactos";
const ContactDetail = lazyWithRetry(() => import("./pages/ContactDetail"), "contact-detail");
const IniciarConversacion = lazyWithRetry(() => import("./pages/IniciarConversacion"), "iniciar-conversacion");
const Plantillas = lazyWithRetry(() => import("./pages/Plantillas"), "plantillas");
const Soporte = lazyWithRetry(() => import("./pages/Soporte"), "soporte");
const Calendario = lazyWithRetry(() => import("./pages/Calendario"), "calendario");
// import Outlook from "./pages/Outlook";
const Email = lazyWithRetry(() => import("./pages/Email"), "email");
const VideoGenerator = lazyWithRetry(() => import("./pages/VideoGenerator"), "video-generator");
const BusinessSearch = lazyWithRetry(() => import("./pages/BusinessSearch"), "business-search");
const NuevoCliente = lazyWithRetry(() => import("./pages/NuevoCliente"), "nuevo-cliente");
const ContactosListas = lazyWithRetry(() => import("./pages/ContactosListas"), "contactos-listas");
const EnviarNovedades = lazyWithRetry(() => import("./pages/EnviarNovedades"), "enviar-novedades");
const Activity = lazyWithRetry(() => import("./pages/Activity"), "activity");
const Tasks = lazyWithRetry(() => import("./pages/Tasks"), "tasks");
const RegistroResults = lazyWithRetry(() => import("./pages/RegistroResults"), "registro-results");
const ConfiguracionEmpresas = lazyWithRetry(() => import("./pages/ConfiguracionEmpresas"), "configuracion-empresas");
const AccessManagement = lazyWithRetry(() => import("./pages/AccessManagement"), "access-management");
const EmailTemplates = lazyWithRetry(() => import("./pages/EmailTemplates"), "email-templates");
const MultimediaManager = lazyWithRetry(() => import("./pages/MultimediaManager"), "multimedia-manager");
const MetricasWhatsApp = lazyWithRetry(() => import("./pages/MetricasWhatsApp"), "metricas-whatsapp");
const TablaCompetencia = lazyWithRetry(() => import("./pages/TablaCompetencia"), "tabla-competencia");
const TutorialGallery = lazyWithRetry(() => import("./pages/TutorialGallery"), "tutorial-gallery");
const SolicitudesDemo = lazyWithRetry(() => import("./pages/SolicitudesDemo"), "solicitudes-demo");
const MisAgendas = lazyWithRetry(() => import("./pages/MisAgendas"), "mis-agendas");
const Televenta = lazyWithRetry(() => import("./pages/Televenta"), "televenta");
const Recomendaciones = lazyWithRetry(() => import("./pages/Recomendaciones"), "recomendaciones");
const SupervisionTablets = lazyWithRetry(() => import("./pages/SupervisionTablets"), "supervision-tablets");
const AgendasConexiones = lazyWithRetry(() => import("./pages/AgendasConexiones"), "agendas-conexiones");
const NumerosDemo = lazyWithRetry(() => import("./pages/NumerosDemo"), "numeros-demo");
const SetterPanel = lazyWithRetry(() => import("./pages/setter/SetterPanel"), "setter-panel");
const FirstChangePassword = lazyWithRetry(() => import("./pages/FirstChangePassword"), "first-change-password");
const CrearAcceso = lazyWithRetry(() => import("./pages/CrearAcceso"), "crear-acceso");

/**
 * Fallback de Suspense mientras se descarga el chunk de una página lazy.
 * Solo se ve en la PRIMERA carga de una ruta (URL directa / F5): en navegación
 * interna react-router mantiene la vista anterior.
 */
function RouteLoadingFallback() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-white dark:bg-gray-900">
      <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" />
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Refrescar el access token este margen ANTES de que expire (proactivo).
const REFRESH_SKEW_SEC = 120;

function SessionLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <span className="inline-block w-4 h-4 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin" />
        Reanudando sesión…
      </div>
    </div>
  );
}

/**
 * ProtectedRoute
 * Valida que exista un token JWT. Si el access token ha caducado pero hay un
 * refresh token, intenta refrescarlo (en vez de cerrar sesión). Además programa
 * un refresco PROACTIVO antes de que caduque, para que una sesión activa no se
 * pierda nunca a la hora. Solo se cierra sesión si NO hay refresh token o si el
 * refresco falla de verdad.
 */
function ProtectedRoute() {
  const location = useLocation();
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  // idle | refreshing | failed
  const [phase, setPhase] = useState("idle");

  // Mantener `token` en sync con localStorage: el interceptor de axios y otras
  // pestañas pueden rotar el token; el evento storage solo dispara en OTRAS
  // pestañas, así que también re-leemos en cada navegación.
  useEffect(() => {
    const sync = () => setToken(localStorage.getItem("token"));
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);
  useEffect(() => {
    const t = localStorage.getItem("token");
    setToken((prev) => (prev === t ? prev : t));
  }, [location.pathname]);

  let decodedToken = null;
  if (token) {
    try {
      decodedToken = jwtDecode(token);
    } catch (_) {
      decodedToken = null;
    }
  }

  const hasRefresh = !!localStorage.getItem("refresh_token");
  const nowSec = Date.now() / 1000;
  const exp = decodedToken?.exp ?? 0;
  const expired = !decodedToken || exp <= nowSec;

  // ── Refresco REACTIVO: token caducado/ilegible pero hay refresh token ──
  // El "ya hay uno en vuelo" va en una REF, no en `phase`, y `phase` NO está en las
  // deps. Motivo (bug real: la sesión se quedaba en "Reanudando sesión…" para
  // siempre): este efecto llama a `setPhase("refreshing")` dentro de sí mismo. Con
  // `phase` en las deps, ese setPhase provocaba un re-render → deps cambiadas →
  // React ejecutaba el CLEANUP del efecto → `cancelled = true` → y al llegar la
  // respuesta, `if (cancelled) return` mataba tanto el `setToken` como el
  // `setPhase("failed")`. La fase se quedaba en "refreshing" → splash eterno.
  //
  // Lo grave era el camino de FALLO: cuando el refresh token está caducado, revocado
  // o el backend responde 401 (p. ej. `reuse_detected`, que borra la familia entera),
  // `refreshAccessToken` limpia los tokens y lanza. Ese `.catch` es lo ÚNICO que pone
  // `phase="failed"`, que es lo que manda al login. Cancelado, en vez de cerrar
  // sesión te dejaba mirando la barra de progreso indefinidamente.
  // Mismo arreglo que en `packages/auth/src/ProtectedRoute.tsx` (esta es su copia).
  const refreshingRef = useRef(false);
  useEffect(() => {
    if (!expired || !hasRefresh || refreshingRef.current) return undefined;
    let cancelled = false;
    refreshingRef.current = true;
    setPhase("refreshing");
    refreshAccessToken({ backendUrl: BACKEND_URL, failedToken: token })
      .then((tok) => {
        refreshingRef.current = false;
        if (cancelled) return;
        setToken(tok);
        setPhase("idle");
      })
      .catch(() => {
        refreshingRef.current = false;
        if (cancelled) return;
        setPhase("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [expired, hasRefresh, token]);

  // ── Refresco PROACTIVO: temporizador antes de exp + al volver a la pestaña ──
  useEffect(() => {
    if (!decodedToken || !hasRefresh) return undefined;
    const proactive = async () => {
      try {
        const tok = await refreshAccessToken({
          backendUrl: BACKEND_URL,
          failedToken: localStorage.getItem("token"),
        });
        setToken(tok);
      } catch (_) {
        /* el guardia reactivo lo gestionará en el próximo render */
      }
    };
    const fireInMs = Math.max(0, (exp - REFRESH_SKEW_SEC) * 1000 - Date.now());
    const timer = setTimeout(proactive, fireInMs);
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const t = localStorage.getItem("token");
      let d = null;
      try {
        d = t ? jwtDecode(t) : null;
      } catch (_) {
        d = null;
      }
      // Temporizadores throttled en pestañas en 2º plano / suspensión: al volver,
      // refresca si el token está caducado o a punto.
      if (!d || d.exp - REFRESH_SKEW_SEC <= Date.now() / 1000) proactive();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [exp, decodedToken?.user_id, hasRefresh]);

  // Inicializa activityTracker si todavía no está activo (caso refresh)
  useEffect(() => {
    if (!decodedToken) return;
    if (!activityTracker.isInitialized()) {
      activityTracker.init({
        user_id: decodedToken.user_id,
        company_id: decodedToken.company_id,
      });
      activityTracker.track("APP_OPEN", { source: "refresh" });
    }
  }, [decodedToken?.user_id, decodedToken?.company_id]);

  // Trackea cada cambio de ruta
  useEffect(() => {
    if (!decodedToken) return;
    if (!activityTracker.isInitialized()) return;
    activityTracker.track("PAGE_VIEW", { path: location.pathname });
  }, [location.pathname, decodedToken?.user_id]);

  // Sin sesión posible (ni token ni refresh) → login
  if (!token && !hasRefresh) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  // Token caducado/ilegible: refrescar si se puede; cerrar sesión solo si no hay
  // refresh o si el refresco ha fallado.
  if (expired) {
    if (phase === "failed" || !hasRefresh) {
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      return <Navigate to="/login" state={{ from: location }} />;
    }
    return <SessionLoader />;
  }

  // Forzar cambio de contraseña si el usuario fue creado por vendedor o tiene must_change_password
  if (
    (decodedToken?.must_change_password ||
      decodedToken?.created_from_vendedor) &&
    location.pathname !== "/first-change-password"
  ) {
    return <Navigate to="/first-change-password" replace />;
  }
  return <Outlet />;
}

function App() {
  // Decodificar token para comprobar permisos de rutas protegidas condicionales
  // let tokenPayload = null;
  // try {
  //   const token = localStorage.getItem("token");
  //   if (token) tokenPayload = jwtDecode(token);
  // } catch (_) {}
  return (
    <Router>
      <UpdateNotification />
      <SessionExpiredModal />
      <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        {/* Rutas públicas */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth-callback" element={<AuthCallback />} />

        {/* Rutas protegidas: se envuelven en ProtectedRoute, Layout y WebSocketProvider */}
        <Route element={<ProtectedRoute />}>
          {/* first-change-password SIN Layout: no Sidebar, no navegación posible */}
          <Route
            path="/first-change-password"
            element={<FirstChangePassword />}
          />
          <Route
            element={
              <WebSocketProvider>
                <AccessProvider>
                  <SimulatorProvider>
                    <Layout />
                  </SimulatorProvider>
                </AccessProvider>
              </WebSocketProvider>
            }
          >
            {/* NO envuelta en RequireAccess a propósito: el override real de producción de
                "dashboard" es 0, y wrappearla restringiría el acceso hoy libre de niveles 1-3
                (rompería a Trini). En su lugar, RequireNotFieldVendor bloquea SOLO al rol
                nuevo (nivel >= FIELD_VENDOR_LEVEL) por el nivel crudo del JWT, no por
                canAccess/sectionId. Ver informe de la tarea 7. */}
            <Route
              path="/dashboard"
              element={
                <RequireNotFieldVendor>
                  <Dashboard />
                </RequireNotFieldVendor>
              }
            />
            <Route
              path="/tareas"
              element={
                <RequireAccess sectionId="tareas">
                  <Tasks />
                </RequireAccess>
              }
            />
            <Route
              path="/personal"
              element={
                <RequireAccess sectionId="configuracion_personal">
                  <Personal />
                </RequireAccess>
              }
            />
            {/* Compatibilidad: redirigir ruta obsoleta ajustes a personal */}
            <Route
              path="/ajustes"
              element={<Navigate to="/personal" replace />}
            />
            {/* <Route path="/automatizaciones" element={<Automatizaciones />} /> */}
            <Route
              path="/campañas"
              element={
                <RequireAccess sectionId="campanas">
                  <Campañas />
                </RequireAccess>
              }
            />
            <Route
              path="/enviar-novedades"
              element={
                <RequireAccess sectionId="enviar_novedades">
                  <EnviarNovedades />
                </RequireAccess>
              }
            />
            <Route
              path="/campañas/seguimiento"
              element={
                <RequireAccess sectionId="campanas">
                  <SeguimientoCampañas />
                </RequireAccess>
              }
            />
            <Route
              path="/campañas/:campaignId"
              element={
                <RequireAccess sectionId="campanas">
                  <SeguimientoCampañas />
                </RequireAccess>
              }
            />
            <Route
              path="/campanas/kanban/:campaignId"
              element={
                <RequireAccess sectionId="campanas">
                  <KanbanCampana />
                </RequireAccess>
              }
            />
            <Route
              path="/chat/:contactId"
              element={
                <RequireAccess sectionId="chats">
                  <Chats />
                </RequireAccess>
              }
            />
            <Route
              path="/chats"
              element={
                <RequireAccess sectionId="chats">
                  <Chats />
                </RequireAccess>
              }
            />
            <Route
              path="/chats/:contactId"
              element={
                <RequireAccess sectionId="chats">
                  <Chats />
                </RequireAccess>
              }
            />
            <Route
              path="/documentos"
              element={
                <RequireAccess sectionId="documentos">
                  <Documentos />
                </RequireAccess>
              }
            />
            <Route
              path="/calendario"
              element={
                <RequireAccess sectionId="calendario">
                  <Calendario />
                </RequireAccess>
              }
            />
            {/* Compat: mantener ruta antigua y redirigir al nuevo módulo unificado */}
            <Route path="/outlook" element={<Navigate to="/email/outlook" />} />
            {/* Nuevas rutas unificadas de Email */}
            <Route
              path="/email/:provider"
              element={
                <RequireAccess sectionId="configuracion_personal">
                  <Email />
                </RequireAccess>
              }
            />
            <Route
              path="/contactos/:contactId"
              element={
                <RequireAccess sectionId="contactos">
                  <ContactDetail />
                </RequireAccess>
              }
            />
            <Route
              path="/contactos"
              element={<Navigate to="/contactos-listas" />}
            />
            <Route
              path="/contactos-listas"
              element={
                <RequireAccess sectionId="contactos">
                  <ContactosListas />
                </RequireAccess>
              }
            />
            <Route
              path="/supervision-vendedores"
              element={<Navigate to="/actividad" replace />}
            />
            <Route
              path="/actividad"
              element={
                <RequireAccess sectionId="actividad">
                  <Activity />
                </RequireAccess>
              }
            />
            <Route
              path="/iniciar-conversacion"
              element={
                <RequireAccess sectionId="chats">
                  <IniciarConversacion />
                </RequireAccess>
              }
            />
            <Route
              path="/plantillas"
              element={
                <RequireAccess sectionId="plantillas">
                  <Plantillas />
                </RequireAccess>
              }
            />
            <Route
              path="/email-templates"
              element={
                <RequireAccess sectionId="plantillas">
                  <EmailTemplates />
                </RequireAccess>
              }
            />
            <Route
              path="/pre-produccion"
              element={
                <RequireAccess sectionId="pre_produccion">
                  <MultimediaManager />
                </RequireAccess>
              }
            />
            <Route
              path="/soporte"
              element={
                <RequireAccess sectionId="soporte">
                  <Soporte />
                </RequireAccess>
              }
            />
            <Route
              path="/generador-videos"
              element={
                <RequireAccess sectionId="pre_produccion">
                  <VideoGenerator />
                </RequireAccess>
              }
            />
            <Route
              path="/buscador-negocios"
              element={
                <RequireAccess sectionId="buscador_negocios">
                  <BusinessSearch />
                </RequireAccess>
              }
            />
            {/* NO envuelta en RequireAccess a propósito: el override real de producción de
                "nuevo_cliente" es 0, y wrappearla restringiría el acceso hoy libre de niveles
                1-3 (rompería a Trini). En su lugar, RequireNotFieldVendor bloquea SOLO al rol
                nuevo (nivel >= FIELD_VENDOR_LEVEL) por el nivel crudo del JWT, no por
                canAccess/sectionId. Ver informe de la tarea 7. */}
            <Route
              path="/nuevo-cliente"
              element={
                <RequireNotFieldVendor>
                  <NuevoCliente />
                </RequireNotFieldVendor>
              }
            />
            <Route
              path="/resultados-registro"
              element={
                <RequireAccess sectionId="resultados_registro">
                  <RegistroResults />
                </RequireAccess>
              }
            />
            <Route
              path="/configuracion-empresas"
              element={
                <RequireAccess sectionId="configuracion_empresas">
                  <ConfiguracionEmpresas />
                </RequireAccess>
              }
            />
            <Route
              path="/gestion-accesos"
              element={
                <RequireAccess sectionId="gestion_accesos">
                  <AccessManagement />
                </RequireAccess>
              }
            />
            <Route
              path="/metricas-whatsapp"
              element={
                <RequireAccess sectionId="metricas_whatsapp">
                  <MetricasWhatsApp />
                </RequireAccess>
              }
            />
            <Route
              path="/tabla-competencia"
              element={
                <RequireAccess sectionId="tabla_competencia">
                  <TablaCompetencia />
                </RequireAccess>
              }
            />
            <Route
              path="/tutoriales"
              element={
                <RequireAccess sectionId="tutorial_gallery">
                  <TutorialGallery />
                </RequireAccess>
              }
            />
            <Route
              path="/solicitudes-demo"
              element={
                <RequireAccess sectionId="solicitudes_demo">
                  <SolicitudesDemo />
                </RequireAccess>
              }
            />
            <Route
              path="/mis-agendas"
              element={
                <RequireAccess sectionId="mis_agendas">
                  <MisAgendas />
                </RequireAccess>
              }
            />
            {/* NO envuelta en RequireAccess a propósito: el override real de producción de
                "televenta" es 0, y wrappearla restringiría el acceso hoy libre de niveles 1-3
                (rompería a Trini). En su lugar, RequireNotFieldVendor bloquea SOLO al rol
                nuevo (nivel >= FIELD_VENDOR_LEVEL) por el nivel crudo del JWT, no por
                canAccess/sectionId. Ver informe de la tarea 7. */}
            <Route
              path="/televenta"
              element={
                <RequireNotFieldVendor>
                  <Televenta />
                </RequireNotFieldVendor>
              }
            />
            <Route
              path="/numeros-demo"
              element={
                <RequireAccess sectionId="numeros_demo">
                  <NumerosDemo />
                </RequireAccess>
              }
            />
            <Route
              path="/recomendaciones"
              element={
                <RequireAccess sectionId="recomendaciones">
                  <Recomendaciones />
                </RequireAccess>
              }
            />
            <Route
              path="/setter-ia"
              element={
                <RequireAccess sectionId="setter_ia">
                  <SetterPanel />
                </RequireAccess>
              }
            />
            <Route
              path="/supervision-tablets"
              element={
                <RequireAccess sectionId="supervision_tablets">
                  <SupervisionTablets />
                </RequireAccess>
              }
            />
            <Route
              path="/agendas-conexiones"
              element={
                <RequireAccess sectionId="agendas_conexiones">
                  <AgendasConexiones />
                </RequireAccess>
              }
            />
            <Route
              path="/crear-acceso"
              element={
                <RequireAccess sectionId="crear_acceso">
                  <CrearAcceso />
                </RequireAccess>
              }
            />
            {/* Ruta catch-all: redirige a la home del nivel del usuario */}
            <Route path="*" element={<Navigate to={currentHomePath()} replace />} />
          </Route>
        </Route>

        {/* Cualquier otra ruta redirige a Login */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
